import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { BannerConfig } from '../../custom-types/banner';
import { RootState } from '../index';

interface BannersState {
  banners: BannerConfig[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}
const initialState: BannersState = {
  banners: [],
  loading: false,
  error: null,
  lastFetched: null,
};

/**
 * Fetch active banners from backend
 * This will retrieve all banner configurations from the database
 */
export const fetchBanners = createAsyncThunk<
  BannerConfig[],
  string,
  { state: RootState }
>('banners/fetchBanners', async (placement, { getState }) => {
  const token = localStorage.getItem('collabLandToken');
  const url = `${process.env.REACT_APP_API_URL}/banners/active?placement=${placement}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch banners');
  }

  const data = await response.json();
  return data;
});

const bannersSlice = createSlice({
  name: 'banners',
  initialState,
  reducers: {
    clearBanners: (state) => {
      state.banners = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBanners.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchBanners.fulfilled,
        (state, action: PayloadAction<BannerConfig[]>) => {
          state.loading = false;
          state.banners = (action.payload || []).sort(
            (a, b) => a.priority - b.priority,
          );
          state.lastFetched = Date.now();
        },
      )
      .addCase(fetchBanners.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch banners';
      });
  },
});

export const { clearBanners } = bannersSlice.actions;
export default bannersSlice.reducer;
